import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ArrowLeft, Clock, Loader2, MessageCircle, Search, User } from "lucide-react";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { useWhatsAppInbox } from "@/contexts/WhatsAppInboxContext";
import { useAuth } from "@/contexts/AuthContext";
import WhatsAppConversation from "@/components/WhatsAppConversation";
import InboxStatusBar from "@/components/InboxStatusBar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const PILL = {
  Hot: "bg-rose-100 text-rose-700",
  Warm: "bg-amber-100 text-amber-700",
  Cool: "bg-sky-100 text-sky-700",
  Cold: "bg-slate-100 text-slate-600",
};

function timeAgo(iso) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 60000;
  if (diff < 1) return "now";
  if (diff < 60) return `${Math.floor(diff)}m`;
  if (diff < 1440) return `${Math.floor(diff / 60)}h`;
  return `${Math.floor(diff / 1440)}d`;
}

export default function WhatsAppInboxPage() {
  const [params, setParams] = useSearchParams();
  const { markLeadRead, refresh } = useWhatsAppInbox();
  const { user } = useAuth();
  const [convos, setConvos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(params.get("lead") || null);
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [adFilter, setAdFilter] = useState("all");
  const [mineOnly, setMineOnly] = useState(false);
  const [waitingSort, setWaitingSort] = useState(false);
  const [customStages, setCustomStages] = useState({});

  useEffect(() => {
    api.get("/admin/pipelines").then((r) => {
      const m = {};
      (r.data?.pipelines || []).forEach((p) => { m[p.id] = (p.stages || []).map((s) => s.label); });
      setCustomStages(m);
    }).catch(() => {});
  }, []);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/whatsapp/inbox");
      setConvos(data.conversations || []);
    } catch {
      setConvos([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 45_000);
    return () => clearInterval(id);
  }, []);

  const open = (leadId) => {
    setActive(leadId);
    setParams({ lead: leadId });
    markLeadRead(leadId);
    setConvos((c) => c.map((x) => (x.lead_id === leadId ? { ...x, unread: 0 } : x)));
    setTimeout(() => { load(); refresh(); }, 600);
  };

  const filtered = useMemo(() => {
    let list = convos;
    if (tab === "needs_reply") list = list.filter((c) => c.needs_reply);
    if (mineOnly && user?.id) list = list.filter((c) => c.assigned_to === user.id);
    if (statusFilter !== "all") list = list.filter((c) => (c.counseling_active ? c.counseling_status : c.status) === statusFilter);
    if (adFilter !== "all") list = list.filter((c) => (c.ad_headline || "—") === adFilter);
    const s = q.trim().toLowerCase();
    if (s) list = list.filter((c) => (c.name || "").toLowerCase().includes(s) || (c.phone || "").includes(s));
    if (waitingSort) {
      list = [...list].sort((a, b) => {
        if (!!a.needs_reply !== !!b.needs_reply) return a.needs_reply ? -1 : 1;
        return new Date(a.last_at || 0) - new Date(b.last_at || 0);
      });
    }
    return list;
  }, [convos, q, tab, statusFilter, adFilter, mineOnly, waitingSort, user]);

  const needsReplyCount = useMemo(() => convos.filter((c) => c.needs_reply).length, [convos]);
  const statusOptions = useMemo(() => {
    const set = new Set();
    convos.forEach((c) => { const s = c.counseling_active ? c.counseling_status : c.status; if (s) set.add(s); });
    return Array.from(set);
  }, [convos]);
  const adOptions = useMemo(() => {
    const set = new Set();
    convos.forEach((c) => { if (c.ad_headline) set.add(c.ad_headline); });
    return Array.from(set);
  }, [convos]);

  const activeConvo = convos.find((c) => c.lead_id === active);

  return (
    <main className="flex h-screen flex-col" data-testid="whatsapp-inbox-page">
      <header className="border-b border-slate-200 bg-white px-6 py-4">
        <h1 className="font-display text-2xl font-bold tracking-tight text-[#0A2540]">WhatsApp Inbox</h1>
        <p className="text-sm text-slate-500">All conversations, newest first. Unread messages are highlighted.</p>
      </header>

      <div className="flex min-h-0 flex-1">
        {/* Conversation list */}
        <div className={`${active ? "hidden md:flex" : "flex"} w-full flex-col border-r border-slate-200 bg-white md:max-w-sm`}>
          <div className="border-b border-slate-100 p-3">
            <div className="mb-2 flex gap-1.5">
              <button
                onClick={() => setTab("all")}
                className={`flex-1 rounded-md px-2 py-1.5 text-xs font-medium transition-colors ${tab === "all" ? "bg-[#0A2540] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
                data-testid="wa-inbox-tab-all"
              >All</button>
              <button
                onClick={() => setTab("needs_reply")}
                className={`flex-1 rounded-md px-2 py-1.5 text-xs font-medium transition-colors ${tab === "needs_reply" ? "bg-rose-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
                data-testid="wa-inbox-tab-needs-reply"
              >
                Needs reply{needsReplyCount > 0 ? ` (${needsReplyCount})` : ""}
              </button>
            </div>
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or phone" className="h-9 pl-8" data-testid="wa-inbox-search" />
            </div>
            <div className="mt-2 flex gap-1.5">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="h-8 flex-1 text-xs" data-testid="wa-inbox-status-filter">
                  <SelectValue placeholder="All stages" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All stages</SelectItem>
                  {statusOptions.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={adFilter} onValueChange={setAdFilter}>
                <SelectTrigger className="h-8 flex-1 text-xs" data-testid="wa-inbox-ad-filter">
                  <SelectValue placeholder="All ad copy" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All ad copy</SelectItem>
                  {adOptions.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="mt-2 flex gap-1.5">
              <button
                onClick={() => setMineOnly((v) => !v)}
                className={`flex flex-1 items-center justify-center gap-1 rounded-md px-2 py-1.5 text-xs font-medium transition-colors ${mineOnly ? "bg-[#0A2540] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
                data-testid="wa-inbox-mine-toggle"
              >
                <User className="h-3.5 w-3.5" /> Assigned to me
              </button>
              <button
                onClick={() => setWaitingSort((v) => !v)}
                className={`flex flex-1 items-center justify-center gap-1 rounded-md px-2 py-1.5 text-xs font-medium transition-colors ${waitingSort ? "bg-rose-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
                data-testid="wa-inbox-waiting-sort-toggle"
              >
                <Clock className="h-3.5 w-3.5" /> Longest waiting
              </button>
            </div>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto">
            {loading ? (
              <div className="py-10 text-center text-sm text-slate-400"><Loader2 className="mr-1.5 inline h-4 w-4 animate-spin" /> Loading…</div>
            ) : filtered.length === 0 ? (
              <div className="px-4 py-10 text-center text-sm text-slate-400" data-testid="wa-inbox-empty">No conversations yet.</div>
            ) : (
              filtered.map((c) => (
                <button
                  key={c.lead_id}
                  onClick={() => open(c.lead_id)}
                  className={`flex w-full items-start gap-3 border-b border-slate-50 px-4 py-3 text-left transition-colors hover:bg-slate-50 ${active === c.lead_id ? "bg-[#E7F0FF]" : ""}`}
                  data-testid={`wa-inbox-convo-${c.lead_id}`}
                >
                  <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#25D366]/10 text-[#1aa851]">
                    <MessageCircle className="h-4 w-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate text-sm font-semibold text-[#0A2540]">{c.name}</span>
                      <span className="shrink-0 text-[10px] text-slate-400">{timeAgo(c.last_at)}</span>
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate text-xs text-slate-500">
                        {c.last_dir === "outbound" ? "You: " : ""}{c.last_preview}
                      </span>
                      {c.unread > 0 && (
                        <span className="shrink-0 rounded-full bg-[#25D366] px-1.5 text-[10px] font-bold text-white" data-testid={`wa-inbox-unread-${c.lead_id}`}>
                          {c.unread}
                        </span>
                      )}
                    </div>
                    {c.temperature && (
                      <span className={`mt-1 inline-block rounded px-1.5 text-[9px] ${PILL[c.temperature] || "bg-slate-100 text-slate-600"}`}>{c.temperature}</span>
                    )}
                    {c.needs_reply && (
                      <span className="mt-1 ml-1 inline-block rounded bg-rose-50 px-1.5 text-[9px] font-medium text-rose-600" data-testid={`wa-needs-reply-${c.lead_id}`}>
                        Needs reply · waited {timeAgo(c.last_at)}
                      </span>
                    )}
                  </div>
                </button>
              ))
            )}
          </div>
        </div>

        {/* Conversation pane */}
        <div className={`${active ? "flex" : "hidden md:flex"} min-w-0 flex-1 flex-col bg-[#F0F2F5] p-0 md:p-4`}>
          {activeConvo ? (
            <div className="flex min-h-0 flex-1 flex-col rounded-none border-0 border-slate-200 bg-white md:rounded-xl md:border">
              <div className="border-b border-slate-100 px-4 py-3">
                <div className="flex items-start gap-2">
                  <button
                    onClick={() => { setActive(null); setParams({}); }}
                    className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 md:hidden"
                    data-testid="wa-inbox-back-btn"
                    aria-label="Back to conversations"
                  >
                    <ArrowLeft className="h-4 w-4" />
                  </button>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-[#0A2540]">{activeConvo.name}</div>
                    <div className="text-xs text-slate-500">{activeConvo.phone}{activeConvo.assigned_to_name ? ` · ${activeConvo.assigned_to_name}` : ""}</div>
                    {activeConvo.ad_headline && (
                      <div className="mt-0.5 truncate text-[11px] text-indigo-500" title={activeConvo.ad_headline}>Ad: {activeConvo.ad_headline}</div>
                    )}
                  </div>
                </div>
                <div className="mt-2">
                  <InboxStatusBar
                    key={active}
                    convo={activeConvo}
                    customStages={customStages}
                    onChanged={() => { load(); refresh(); }}
                  />
                </div>
              </div>
              <div className="min-h-0 flex-1 p-3">
                <WhatsAppConversation key={active} leadId={active} leadName={activeConvo.name} />
              </div>
            </div>
          ) : (
            <div className="flex flex-1 items-center justify-center text-sm text-slate-400">
              Select a conversation to view messages.
            </div>
          )}
        </div>
      </div>
    </main>
  );
}
